import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react"

import { scheduledJobLabel } from "../lib/scheduled-job-display"
import {
  ScheduleConflictError,
  useUpdateScheduledJob,
  type ScheduledJob,
} from "../services/use-scheduled-jobs"

type Notice = { message: string; undo?: () => void }
type Actions = {
  notify: (notice: Notice) => void
  toggle: (job: ScheduledJob) => void
  pendingId: string | null
  error: string | null
  clearError: () => void
}
const ScheduleActionsContext = createContext<Actions | null>(null)

export function ScheduleActionsProvider({ children }: { children: ReactNode }) {
  const [notice, setNotice] = useState<Notice | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const busy = useRef(false)
  const update = useUpdateScheduledJob()
  useEffect(() => {
    if (!notice) {
      return
    }
    const timeout = setTimeout(() => setNotice(null), notice.undo ? 5000 : 2400)
    return () => clearTimeout(timeout)
  }, [notice])
  const changeEnabled = (job: ScheduledJob, enabled: boolean, isUndo: boolean) => {
    if (busy.current || (job.manualOnly && enabled)) {
      return
    }
    busy.current = true
    setPendingId(job.id)
    setError(null)
    setNotice(null)
    update.mutate(
      { id: job.id, expectedVersion: job.schedule.version, enabled },
      {
        onSuccess: ({ current }) =>
          setNotice(
            isUndo
              ? { message: "Change undone" }
              : {
                  message: `${scheduledJobLabel(job.id)} ${enabled ? "resumed" : "paused"}`,
                  undo: () => changeEnabled(current, job.enabled, true),
                }
          ),
        onError: (cause) =>
          setError(
            cause instanceof ScheduleConflictError
              ? "This schedule changed in another session. Latest data is reloading. Check it before trying again."
              : `Schedule could not be changed: ${cause.message}`
          ),
        onSettled: () => {
          busy.current = false
          setPendingId(null)
        },
      }
    )
  }
  return (
    <ScheduleActionsContext
      value={{
        notify: setNotice,
        toggle: (job) => changeEnabled(job, !job.enabled, false),
        pendingId,
        error,
        clearError: () => setError(null),
      }}
    >
      {children}

      {notice ? (
        <div className="schedule-toast" role="status">
          <span>{notice.message}</span>

          {notice.undo ? (
            <button type="button" onClick={notice.undo}>
              Undo
            </button>
          ) : null}
        </div>
      ) : null}
    </ScheduleActionsContext>
  )
}

export function useScheduleActions() {
  const actions = useContext(ScheduleActionsContext)
  if (!actions) {
    throw new Error("Schedule actions require the schedules layout")
  }
  return actions
}
